import lodashGet from "lodash/get";
import React, { type ComponentPropsWithoutRef } from "react";
import { type PillarTableBaseColumnCellValuePathProps } from "shared/components/pillar-table/columns/PillarTableColumn";
import { getDefaultAddressContactInformationPublicSetting } from "shared/components/pillar-table/helpers/getDefaultAddressContactInformationPublicSetting";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { useSocietySession } from "shared/context/SocietySessionContext";
import { defaultContactInformationFromProfile } from "shared/helpers/profile/defaultContactInformationFromProfile";
import { type ContactInformationView } from "shared/mappers/database/profile/contact-information";
import { type ProfileView } from "shared/mappers/database/profile/profile";

export type PillarTableColumnProfileContactInformationProps<T> =
  PillarTableBaseColumnCellValuePathProps<T> &
    ComponentPropsWithoutRef<"div"> & {
      property: keyof ContactInformationView;
      use?: string;
      type?: string;
      notBlankProperty?: "email" | "phone";
    };

const PillarTableColumnProfileContactInformation = <T,>({
  cellValuePath,
  property,
  use,
  type,
  className,
  notBlankProperty,
  ...props
}: PillarTableColumnProfileContactInformationProps<T>) => {
  const session = useCustomSession();
  const { rowValue } = usePillarTableRowContext();

  const profile = cellValuePath
    ? (lodashGet(rowValue, cellValuePath) as ProfileView)
    : (rowValue as ProfileView);

  const defaultContactInformationSetting =
    getDefaultAddressContactInformationPublicSetting({
      society: session.society,
    });

  const contactInformation = defaultContactInformationFromProfile({
    profile,
    use: use ?? defaultContactInformationSetting?.use,
    type: type ?? defaultContactInformationSetting?.type,
    notBlankProperty,
  });

  const value = contactInformation?.[property]?.toString() ?? "";

  return (
    <div className={className} {...props}>
      {value}
    </div>
  );
};

export default PillarTableColumnProfileContactInformation;
